// Set-a-new-password screen, mounted when the user arrives via a Supabase
// password-reset email link. App.tsx routes here whenever `auth.recovery` is
// true so a one-time recovery session never silently drops the user into
// Discover. On success, updatePassword() clears the recovery flag and the
// user is signed in normally.

import { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { getAuth } from '@/lib/auth/auth';
import { C } from '@/styles/tokens';

const MIN_PASSWORD = 8;

type Status =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'error'; message: string };

function friendlyError(raw: string): string {
  const m = raw.toLowerCase();
  if (m.includes('password should be at least'))
    return `Password must be at least ${MIN_PASSWORD} characters.`;
  if (m.includes('same as the old password') || m.includes('new password should be different'))
    return 'Pick a password you haven’t used before.';
  return raw;
}

export function ResetPasswordScreen() {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [status, setStatus] = useState<Status>({ kind: 'idle' });

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (password.length < MIN_PASSWORD) {
      setStatus({ kind: 'error', message: `Password must be at least ${MIN_PASSWORD} characters.` });
      return;
    }
    if (password !== confirm) {
      setStatus({ kind: 'error', message: 'The two passwords don’t match.' });
      return;
    }
    setStatus({ kind: 'submitting' });
    try {
      await getAuth().updatePassword(password);
      // updatePassword() clears the recovery flag, which triggers App.tsx to
      // re-render and unmount this screen. Nothing more to do here.
    } catch (err) {
      setStatus({ kind: 'error', message: friendlyError((err as Error).message) });
    }
  }

  return (
    <section
      className="h-[100dvh] flex flex-col items-center justify-between px-6 pt-12 pb-8 safe-top safe-bottom"
      style={{ background: C.cream }}
    >
      <div className="flex-1 flex flex-col items-center justify-center w-full max-w-sm">
        <div className="flex flex-col items-center text-center mb-6">
          <div
            aria-hidden
            className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6"
            style={{
              background: C.vermillion,
              boxShadow: '0 6px 18px rgba(233,75,60,0.32)',
            }}
          >
            <span className="ff-zh-serif text-white text-3xl font-bold leading-none">字</span>
          </div>
          <h1 className="ff-display italic text-3xl mb-3" style={{ color: C.ink }}>
            Set a new password
          </h1>
          <p className="ff-body text-[13px] leading-relaxed max-w-[300px]" style={{ color: C.inkLight }}>
            Pick a new password to finish the reset. You’ll be signed in once it’s saved.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="w-full">
            <label
              htmlFor="reset-password"
              className="block ff-body text-[10px] uppercase tracking-[0.25em] mb-2"
              style={{ color: C.inkLight }}
            >
              New password
            </label>
            <div className="relative mb-3">
              <input
                id="reset-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={`At least ${MIN_PASSWORD} characters`}
                autoComplete="new-password"
                required
                minLength={MIN_PASSWORD}
                className="w-full px-3.5 py-3 pr-11 rounded-2xl border ff-body text-[14px] outline-none"
                style={{
                  background: 'white',
                  borderColor: password ? C.vermillion : 'rgba(0,0,0,0.08)',
                  color: C.ink,
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                aria-pressed={showPassword}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full"
                style={{ color: C.inkLight }}
              >
                {showPassword ? <EyeOff size={16} aria-hidden /> : <Eye size={16} aria-hidden />}
              </button>
            </div>

            <label
              htmlFor="reset-confirm"
              className="block ff-body text-[10px] uppercase tracking-[0.25em] mb-2"
              style={{ color: C.inkLight }}
            >
              Confirm password
            </label>
            <input
              id="reset-confirm"
              type={showPassword ? 'text' : 'password'}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Repeat your new password"
              autoComplete="new-password"
              required
              minLength={MIN_PASSWORD}
              className="w-full px-3.5 py-3 rounded-2xl border ff-body text-[14px] outline-none mb-3"
              style={{
                background: 'white',
                borderColor: confirm ? C.vermillion : 'rgba(0,0,0,0.08)',
                color: C.ink,
              }}
            />

            <button
              type="submit"
              disabled={status.kind === 'submitting' || !password || !confirm}
              className="w-full py-3 rounded-2xl ff-body text-[13px] font-bold uppercase tracking-[0.2em] text-white disabled:opacity-50"
              style={{ background: C.vermillion }}
            >
              {status.kind === 'submitting' ? '…' : 'Save password'}
            </button>

            {status.kind === 'error' && (
              <p
                role="alert"
                className="ff-body text-[11px] mt-3 px-1"
                style={{ color: C.vermillionDeep }}
              >
                {status.message}
              </p>
            )}
        </form>
      </div>
    </section>
  );
}
